import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import {
  usePlaylists,
  useCreatePlaylist,
  useUpdatePlaylist,
  useDeletePlaylist,
  useGenerateCustomList,
  useGenerateAIRecommendations,
} from '../hooks/usePlaylists';
import { useAuth } from '../AuthContext';

const PlaylistsContext = createContext();

export const usePlaylistsContext = () => {
  const context = useContext(PlaylistsContext);
  if (!context) {
    throw new Error('usePlaylistsContext must be used within PlaylistsProvider');
  }
  return context;
};

export const PlaylistsProvider = ({ children }) => {
  // Get authentication status
  const { isAuthenticated } = useAuth();
  
  // React Query hooks - only fetch when authenticated
  const { data: playlists = [], isLoading, error, refetch } = usePlaylists(isAuthenticated);
  const createPlaylistMutation = useCreatePlaylist();
  const updatePlaylistMutation = useUpdatePlaylist();
  const deletePlaylistMutation = useDeletePlaylist();
  const generateCustomListMutation = useGenerateCustomList();
  const generateAIRecsMutation = useGenerateAIRecommendations();

  // Local state
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const [searchEventTerm, setSearchEventTerm] = useState('');
  const [debouncedSearchEventTerm, setDebouncedSearchEventTerm] = useState('');
  const [hidePastEvents, setHidePastEvents] = useState(true); // Default: hide past events
  const [showOnlyNoDJ, setShowOnlyNoDJ] = useState(false); // Filter: show only events without DJ
  const [displayLimit, setDisplayLimit] = useState(20); // Pagination
  
  // Debounce search term to reduce unnecessary filtering
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchEventTerm(searchEventTerm);
    }, 300);
    
    return () => {
      clearTimeout(handler);
    };
  }, [searchEventTerm]);

  // Normalize text for accent-insensitive search
  const normalizeText = useCallback((text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }, []);

  // Filtered playlists (using debounced search and hide past events filter)
  const allFilteredPlaylists = useMemo(() => {
    let filtered = playlists;

    // Filter by hide past events (show yesterday and onwards by default)
    if (hidePastEvents) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0); // Start of yesterday
      
      filtered = filtered.filter(playlist => {
        if (!playlist.fecha_evento) return true; // Keep events without date
        const eventDate = new Date(playlist.fecha_evento);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= yesterday; // Keep yesterday and future events
      });
    }

    // Filter by "Sin DJ asignado"
    if (showOnlyNoDJ) {
      filtered = filtered.filter(playlist => {
        const dj = playlist.nombre_dj;
        return !dj || dj.trim() === '';
      });
    }

    // Filter by search term
    if (debouncedSearchEventTerm) {
      const searchNormalized = normalizeText(debouncedSearchEventTerm);

      filtered = filtered.filter(playlist => {
        // Search in nombre_novios
        if (normalizeText(playlist.nombre_novios || '').includes(searchNormalized)) return true;
        
        // Search in lugar
        if (normalizeText(playlist.lugar_evento || '').includes(searchNormalized)) return true;
        
        // Search in DJ name
        if (normalizeText(playlist.nombre_dj || '').includes(searchNormalized)) return true;
        
        // Search in asistente (fotomatón/360)
        if (normalizeText(playlist.nombre_asistente || '').includes(searchNormalized)) return true;
        
        return false;
      });
    }

    // Ordenar por fecha: eventos más próximos primero, y si coinciden, por hora de inicio
    return filtered.sort((a, b) => {
      // Si alguno no tiene fecha, ponerlo al final
      if (!a.fecha_evento && !b.fecha_evento) return 0;
      if (!a.fecha_evento) return 1; // a sin fecha va al final
      if (!b.fecha_evento) return -1; // b sin fecha va al final
      
      // Comparar fechas: más próximos primero (ascendente)
      const dateA = new Date(a.fecha_evento);
      const dateB = new Date(b.fecha_evento);
      const dateDiff = dateA - dateB;
      
      // Si las fechas son diferentes, ordenar por fecha
      if (dateDiff !== 0) return dateDiff;
      
      // Si las fechas son iguales, ordenar por hora de inicio
      const timeA = a.horario_inicio || '';
      const timeB = b.horario_inicio || '';
      
      // Si ambos tienen hora, comparar numéricamente
      if (timeA && timeB) {
        // Convertir hora "HH:MM" a minutos, considerando que el día empieza a las 08:00
        // Las horas 00:00-07:59 se consideran "después de las 23:59" (madrugada = fin de la noche)
        const parseTime = (timeStr) => {
          const [hours, minutes] = timeStr.split(':').map(Number);
          let totalMinutes = hours * 60 + minutes;
          
          // Si la hora es antes de las 08:00, sumarle 24 horas (1440 minutos)
          // para que aparezca después de las 23:59
          if (hours < 8) {
            totalMinutes += 1440; // 24 * 60
          }
          
          return totalMinutes;
        };
        
        const minutesA = parseTime(timeA);
        const minutesB = parseTime(timeB);
        
        return minutesA - minutesB;
      }
      
      // Si solo uno tiene hora, ponerlo primero
      if (timeA && !timeB) return -1;
      if (!timeA && timeB) return 1;
      
      // Si ninguno tiene hora, mantener orden original
      return 0;
    });
  }, [playlists, debouncedSearchEventTerm, hidePastEvents, showOnlyNoDJ, normalizeText]);

  // Paginated playlists - only show limited number
  const filteredPlaylists = useMemo(() => {
    return allFilteredPlaylists.slice(0, displayLimit);
  }, [allFilteredPlaylists, displayLimit]);

  // Check if there are more events to load
  const hasMoreEvents = allFilteredPlaylists.length > displayLimit;

  // Load more events
  const loadMoreEvents = useCallback(() => {
    setDisplayLimit(prev => prev + 20);
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setDisplayLimit(20);
  }, [debouncedSearchEventTerm, hidePastEvents, showOnlyNoDJ]);

  // Get playlist by ID
  const getPlaylistById = useCallback((playlistId) => {
    return playlists.find(p => p.id === playlistId);
  }, [playlists]);

  // Select playlist and set as current
  const selectPlaylist = useCallback((playlistId) => {
    const playlist = getPlaylistById(playlistId);
    setCurrentPlaylist(playlist || null);
    return playlist;
  }, [getPlaylistById]);

  // Count events without DJ (for dashboard alert)
  const eventsWithoutDJCount = useMemo(() => {
    // Only count future events without DJ
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return playlists.filter(playlist => {
      // Check if event is in the future
      if (playlist.fecha_evento) {
        const eventDate = new Date(playlist.fecha_evento);
        eventDate.setHours(0, 0, 0, 0);
        if (eventDate < today) return false; // Skip past events
      }
      // Check if no DJ assigned
      const dj = playlist.nombre_dj;
      return !dj || dj.trim() === '';
    }).length;
  }, [playlists]);

  const value = {
    // Data
    playlists,
    filteredPlaylists,
    currentPlaylist,
    isLoading,
    error,

    // Search
    searchEventTerm,
    setSearchEventTerm,

    // Filters
    hidePastEvents,
    setHidePastEvents,
    showOnlyNoDJ,
    setShowOnlyNoDJ,
    
    // Stats
    eventsWithoutDJCount,

    // Pagination
    hasMoreEvents,
    loadMoreEvents,
    totalFilteredCount: allFilteredPlaylists.length,
    displayedCount: filteredPlaylists.length,

    // Actions
    refetchPlaylists: refetch,
    fetchPlaylists: refetch,  // alias (varios componentes usan este nombre)
    createPlaylist: createPlaylistMutation.mutateAsync,
    updatePlaylist: updatePlaylistMutation.mutateAsync,
    deletePlaylist: deletePlaylistMutation.mutateAsync,
    generateCustomList: generateCustomListMutation.mutateAsync,
    generateAIRecommendations: generateAIRecsMutation.mutateAsync,
    
    // Playlist selection
    setCurrentPlaylist,
    selectPlaylist,
    getPlaylistById,

    // Mutations loading states
    isCreating: createPlaylistMutation.isPending,
    isUpdating: updatePlaylistMutation.isPending,
    isDeleting: deletePlaylistMutation.isPending,
    isGeneratingCustomList: generateCustomListMutation.isPending,
    isGeneratingAIRecs: generateAIRecsMutation.isPending,
  };

  return <PlaylistsContext.Provider value={value}>{children}</PlaylistsContext.Provider>;
};
